import { create } from "zustand";
import { CollabageddonProduct } from "./CollabageddonProductCard";

export type CollabageddonMode = null | "six" | "all";

export type CollabageddonStoreState = {
  mode: CollabageddonMode;
  selectedProducts: CollabageddonProduct[];
  maxSelections: number;
  allProductsAdded: boolean;
};

type CollabageddonStoreActions = {
  setMode: (mode: CollabageddonMode) => void;
  addProduct: (product: CollabageddonProduct) => void;
  removeProduct: (productId: number) => void;
  clearSelection: () => void;
  addAllProducts: (products: CollabageddonProduct[]) => void;
  isProductSelected: (productId: number) => boolean;
  canAddMore: () => boolean;
  isSelectionComplete: () => boolean;
  reset: () => void;
};

const initialCollabageddonStoreState: CollabageddonStoreState = {
  mode: null,
  selectedProducts: [],
  maxSelections: 6,
  allProductsAdded: false,
};

export const useCollabageddonStore = create<
  CollabageddonStoreState & CollabageddonStoreActions
>((set, get) => ({
  ...initialCollabageddonStoreState,

  setMode: (mode) =>
    set((state) => ({
      mode,
      selectedProducts: !mode ? [] : state.selectedProducts,
      allProductsAdded: !mode ? false : state.allProductsAdded,
    })),

  addProduct: (product) =>
    set((state) => {
      if (state.mode !== "six") return state;
      if (state.selectedProducts.length >= state.maxSelections) return state;
      if (state.selectedProducts.find((p) => p.id === product.id)) return state;

      return {
        selectedProducts: [...state.selectedProducts, product],
      };
    }),

  removeProduct: (productId) =>
    set((state) => ({
      selectedProducts: state.selectedProducts.filter(
        (p) => p.id !== productId,
      ),
    })),

  clearSelection: () => set({ selectedProducts: [], allProductsAdded: false }),

  addAllProducts: (products) =>
    set({
      mode: "all",
      selectedProducts: products,
      allProductsAdded: true,
    }),

  isProductSelected: (productId) =>
    get().selectedProducts.some((p) => p.id === productId),

  canAddMore: () => {
    const state = get();
    return (
      state.mode === "six" &&
      state.selectedProducts.length < state.maxSelections
    );
  },

  isSelectionComplete: () => {
    const state = get();
    return (
      (state.mode === "six" &&
        state.selectedProducts.length === state.maxSelections) ||
      (state.mode === "all" && state.allProductsAdded)
    );
  },

  reset: () => set(initialCollabageddonStoreState),
}));
